import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';

export type DealStatus = 'draft' | 'proposed' | 'agreed';

export const DEAL_STATUS_LABEL: Record<DealStatus, string> = {
  draft: 'Draft',
  proposed: 'Proposed to the brand',
  agreed: 'Agreed',
};

export interface DealLine {
  id: number;
  deal: number;
  rate: number | null;
  rate_key?: string;
  bundle: number | null;
  bundle_key?: string;
  moment: number | null;
  moment_name?: string;
  label: string;
  qty: number;
  /** Decimal string — the price on this sheet (may differ from the card). */
  unit_price: string;
  /** Current rate-card price, decimal string, null if the card line is gone. */
  list_unit_price: string | null;
  total: string;
  note: string;
  sort_order: number;
  asset_type?: string;
  category?: string;
  period?: string;
  updated_at: string;
}

export interface BrandDeal {
  id: number;
  brand: string;
  brand_name: string;
  year: number;
  status: DealStatus;
  intro: string;
  stock_discount: string;
  online_discount: string;
  sell_in_target: string;
  sell_out_target: string;
  promo_periods: string;
  settlement_note: string;
  agreed_total: string | null;
  show_prices_publicly: boolean;
  notes: string;
  share_enabled: boolean;
  /** "/deal/<token>" when sharing is on, else "". */
  public_path: string;
  lines: DealLine[];
  lines_total: string;
  list_total: string;
  updated_at: string;
}

export interface ReachSummary {
  configured: boolean;
  error?: string;
  weeks: number;
  through?: string | null;
  page_reach: Record<string, { avg_weekly_sessions: number; weeks: number }>;
  placement_types: Record<string, { avg_weekly_views: number; avg_weekly_clicks: number; ctr: number | null; weeks: number }>;
}

const dealsKey = (brand: string, year: number) => ['brand-deals', brand, year] as const;
const dealKey = (id: number) => ['brand-deal', id] as const;

export const useBrandDeals = (brand: string | undefined, year: number) =>
  useQuery<BrandDeal[]>({
    queryKey: dealsKey(brand ?? '', year),
    queryFn: async () => (await axios.get(`api/brand-deals/?brand=${brand}&year=${year}`)).data,
    enabled: !!brand,
  });

export const useBrandDeal = (id: number | undefined) =>
  useQuery<BrandDeal>({
    queryKey: dealKey(id ?? 0),
    queryFn: async () => (await axios.get(`api/brand-deals/${id}/`)).data,
    enabled: !!id,
  });

export const useReachSummary = () =>
  useQuery<ReachSummary>({
    queryKey: ['brand-deals', 'reach'],
    queryFn: async () => (await axios.get('api/brand-deals/reach/')).data,
    staleTime: 60 * 60 * 1000,
  });

type DealInput = Partial<Omit<BrandDeal, 'id' | 'lines' | 'lines_total' | 'list_total' | 'updated_at' | 'public_path' | 'share_enabled' | 'brand_name'>>;
type LineInput = Partial<Omit<DealLine, 'id' | 'updated_at' | 'total' | 'list_unit_price' | 'rate_key' | 'bundle_key' | 'moment_name' | 'asset_type' | 'category' | 'period'>>;

export const useDealMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['brand-deals'] });
    qc.invalidateQueries({ queryKey: ['brand-deal'] });
    qc.invalidateQueries({ queryKey: ['moments'] });
  };
  const create = useMutation({
    mutationFn: async (payload: DealInput & { brand: string; year: number }): Promise<BrandDeal> =>
      (await axios.post('api/brand-deals/', payload)).data,
    onSuccess: invalidate,
  });
  const patch = useMutation({
    mutationFn: async ({ id, ...payload }: DealInput & { id: number }): Promise<BrandDeal> =>
      (await axios.patch(`api/brand-deals/${id}/`, payload)).data,
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (id: number) => {
      await axios.delete(`api/brand-deals/${id}/`);
    },
    onSuccess: invalidate,
  });
  const share = useMutation({
    mutationFn: async ({ id, on }: { id: number; on: boolean }): Promise<BrandDeal> =>
      (await axios.post(`api/brand-deals/${id}/${on ? 'share' : 'unshare'}/`)).data,
    onSuccess: invalidate,
  });
  const addLine = useMutation({
    mutationFn: async (payload: LineInput & { deal: number }): Promise<DealLine> =>
      (await axios.post('api/brand-deal-lines/', payload)).data,
    onSuccess: invalidate,
  });
  const patchLine = useMutation({
    mutationFn: async ({ id, ...payload }: LineInput & { id: number }): Promise<DealLine> =>
      (await axios.patch(`api/brand-deal-lines/${id}/`, payload)).data,
    onSuccess: invalidate,
  });
  const removeLine = useMutation({
    mutationFn: async (id: number) => {
      await axios.delete(`api/brand-deal-lines/${id}/`);
    },
    onSuccess: invalidate,
  });
  return { create, patch, remove, share, addLine, patchLine, removeLine };
};

/** Theme slot type reported by the storefront for each mosy placement type. */
export const THEME_TYPE_BY_ASSET_TYPE: Record<string, string> = {
  'Hero Banner 100%': 'hero',
  'Hero Banner 50%': 'hero',
  'Homepage Product Grid': 'product-grid',
  'Homepage Promotional Banner': 'promo-banner',
  'Homepage Product Carousel': 'product-carousel',
  'Homepage Brand Logo Carousel': 'brand-carousel',
  'Homepage Browse by Room': 'browse-by-room',
  'Navigation Bar': 'navigation-bar',
  'Banner on PDP': 'pdp-banner',
  'Collection Page': 'collection-page',
  'Featured products on collection': 'collection-featured',
};
export const PAGE_TYPE_BY_CATEGORY: Record<string, string> = { Homepage: 'index', Catalog: 'collection' };

/** One-line reach sentence for a placement, or null when nothing is measured. */
export const reachLine = (
  reach: ReachSummary | undefined,
  assetType: string | undefined,
  category: string | undefined,
): string | null => {
  if (!reach?.configured) return null;
  const parts: string[] = [];
  const page = category ? reach.page_reach[PAGE_TYPE_BY_CATEGORY[category] ?? ''] : undefined;
  if (page?.avg_weekly_sessions) {
    parts.push(`~${page.avg_weekly_sessions.toLocaleString('en-GB')} visits/week on the ${category === 'Homepage' ? 'homepage' : 'collection pages'}`);
  }
  const t = assetType ? reach.placement_types[THEME_TYPE_BY_ASSET_TYPE[assetType] ?? ''] : undefined;
  if (t?.avg_weekly_views) {
    parts.push(`${t.avg_weekly_views.toLocaleString('en-GB')} views/week measured${t.ctr != null ? `, ${(t.ctr * 100).toFixed(1)}% click-through` : ''}`);
  }
  return parts.length ? parts.join(' · ') : null;
};
